import React, { useState, useEffect } from 'react';
import { getAuditLogs } from '../lib/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Card,
  CardContent,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { ScrollText, RefreshCw, Play, StopCircle, User, Zap, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import usePageTitle from '../hooks/usePageTitle';

// European date format
const formatEuropeanDateTime = (dateString) => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  } catch {
    return '—';
  }
};

const AuditLogs = () => {
  usePageTitle('Regjistri i Auditimit');
  
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('all');
  const [targetFilter, setTargetFilter] = useState('all');
  const [selectedLog, setSelectedLog] = useState(null);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (actionFilter !== 'all') params.action = actionFilter;
      if (targetFilter !== 'all') params.target_type = targetFilter;
      
      const response = await getAuditLogs(params);
      setLogs(Array.isArray(response.data) ? response.data : response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      toast.error('Gabim në ngarkimin e regjistrit');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [actionFilter, targetFilter]);

  const getActionBadgeColor = (action) => {
    if (action.includes('remote_start')) return 'bg-emerald-500/20 text-emerald-400';
    if (action.includes('remote_stop')) return 'bg-orange-500/20 text-orange-400';
    if (action.includes('created') || action.includes('registered')) return 'bg-green-500/10 text-green-400';
    if (action.includes('deleted')) return 'bg-red-500/10 text-red-400';
    if (action.includes('updated') || action.includes('changed')) return 'bg-blue-500/10 text-blue-400';
    if (action.includes('login')) return 'bg-purple-500/10 text-purple-400';
    if (action.includes('topup') || action.includes('payment')) return 'bg-yellow-500/10 text-yellow-400';
    if (action.includes('force')) return 'bg-red-500/20 text-red-400';
    return 'bg-zinc-500/10 text-zinc-400';
  };

  const getActionIcon = (action) => {
    if (action.includes('remote_start')) return <Play className="w-4 h-4 text-emerald-400" />;
    if (action.includes('remote_stop')) return <StopCircle className="w-4 h-4 text-orange-400" />;
    if (action.includes('login') || action.includes('user')) return <User className="w-4 h-4 text-purple-400" />;
    if (action.includes('charger') || action.includes('connector')) return <Zap className="w-4 h-4 text-yellow-400" />;
    return null;
  };

  const getActionLabel = (action) => {
    const labels = {
      'remote_start_initiated': '▶️ Karikimi u nis (Admin)',
      'remote_stop_initiated': '⏹️ Karikimi u ndal (Admin)',
      'user_registered': 'Përdorues i ri',
      'user_login': 'Identifikim',
      'site_created': 'Vendndodhje e re',
      'charger_created': 'Karikues i ri',
      'cash_topup': '💵 Mbushje Cash nga Moderator',
      'credits_used': '⚡ Kredite të harxhuara',
      'session_payment': '💳 Pagesë Sesioni',
      'wallet_topup': '💰 Mbushje Portofoli',
      'stripe_topup_completed': '💳 Mbushje Online (Stripe)',
      'credits_added_by_admin': '💵 Kredite shtuar nga Admin',
      'pricing_updated': 'Çmime përditësuar',
      'force_command_sent': '⚡ Komandë Force',
      'charger_restarted': '🔄 Restart karikuesi',
      'backup_created': '💾 Backup i krijuar',
      'client_created_by_admin': '👤 Klient i shtuar nga Admin',
      'moderator_client_password_reset': '🔑 Reset fjalëkalimi klienti'
    };
    return labels[action] || action;
  };

  const formatDetails = (details) => {
    if (!details) return '—';
    
    // Credits used formatting
    if (details.cost_lek !== undefined && details.energy_kwh !== undefined) {
      const parts = [];
      if (details.energy_kwh) parts.push(`⚡ ${parseFloat(details.energy_kwh).toFixed(2)} kWh`);
      if (details.cost_lek) parts.push(`💰 ${parseFloat(details.cost_lek).toFixed(0)} Kredite`);
      if (details.duration_minutes) parts.push(`⏱ ${details.duration_minutes} min`);
      if (details.balance_after !== undefined) parts.push(`Gjendje: ${parseFloat(details.balance_after).toFixed(0)} Kredite`);
      return parts.join(' | ');
    }
    
    // Cash topup formatting
    if (details.amount !== undefined && details.new_balance !== undefined) {
      return `+${details.amount} Kredite | Gjendje: ${parseFloat(details.new_balance).toFixed(0)} Kredite`;
    }
    
    // Special formatting for remote start/stop
    if (details.admin_initiated || details.admin_stopped) {
      const parts = [];
      if (details.charger_id) parts.push(`Karikues: ${details.charger_id}`);
      if (details.connector_id) parts.push(`Porta: #${details.connector_id}`);
      if (details.session_user_name) parts.push(`Klienti: ${details.session_user_name}`);
      if (details.stopped_by_name) parts.push(`Ndaluar nga: ${details.stopped_by_name}`);
      if (details.initiated_by_name) parts.push(`Nisur nga: ${details.initiated_by_name}`);
      if (details.energy_kwh) parts.push(`Energji: ${details.energy_kwh.toFixed(2)} kWh`);
      if (details.cost_lek) parts.push(`Kosto: ${details.cost_lek.toFixed(0)} Kredite`);
      return parts.join(' | ');
    }
    
    return JSON.stringify(details).slice(0, 80);
  };

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
            <ScrollText className="w-5 h-5 text-purple-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white font-outfit">Regjistri i Auditimit</h1>
            <p className="text-zinc-400 text-sm">Historia e veprimeve të adminit në sistem</p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-56 bg-zinc-800/50" data-testid="action-filter">
              <SelectValue placeholder="Filtro sipas veprimit" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">
              <SelectItem value="all">Të gjitha veprimet</SelectItem>
              <SelectItem value="cash_topup">💵 Mbushje Cash nga Moderator</SelectItem>
              <SelectItem value="credits_used">⚡ Kredite të harxhuara</SelectItem>
              <SelectItem value="wallet_topup">💰 Mbushje Portofoli</SelectItem>
              <SelectItem value="remote_start_initiated">▶️ Karikime të nisura (Admin)</SelectItem>
              <SelectItem value="remote_stop_initiated">⏹️ Karikime të ndaluara (Admin)</SelectItem>
              <SelectItem value="force_command_sent">⚡ Komanda Force</SelectItem>
              <SelectItem value="user_registered">Regjistrime</SelectItem>
              <SelectItem value="charger_created">Karikues krijuar</SelectItem>
              <SelectItem value="backup_created">💾 Backup</SelectItem>
              <SelectItem value="pricing_updated">Çmime përditësuar</SelectItem>
            </SelectContent>
          </Select>
          <Select value={targetFilter} onValueChange={setTargetFilter}>
            <SelectTrigger className="w-40 bg-zinc-800/50" data-testid="target-filter">
              <SelectValue placeholder="Objekti" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">
              <SelectItem value="all">Të gjitha</SelectItem>
              <SelectItem value="session">Seanca</SelectItem>
              <SelectItem value="charger">Karikues</SelectItem>
              <SelectItem value="user">Përdorues</SelectItem>
              <SelectItem value="site">Vendndodhje</SelectItem>
              <SelectItem value="wallet">Portofol</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchLogs} data-testid="refresh-logs-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Rifresko
          </Button>
        </div>
      </div>

      <Card className="bg-zinc-900/50 border-white/10">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-zinc-500" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-zinc-400 w-40">Koha</TableHead>
                  <TableHead className="text-zinc-400 w-56">Veprimi</TableHead>
                  <TableHead className="text-zinc-400">Aktori</TableHead>
                  <TableHead className="text-zinc-400 w-24">Roli</TableHead>
                  <TableHead className="text-zinc-400">Detaje</TableHead>
                  <TableHead className="text-zinc-400 w-20"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-zinc-500 py-12">
                      <ScrollText className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                      <p>Nuk ka regjistrime të gjendura</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow 
                      key={log.id} 
                      className={`border-white/5 hover:bg-white/5 ${
                        log.action.includes('remote_') ? 'bg-zinc-800/30' : ''
                      }`}
                    >
                      <TableCell className="text-zinc-400 text-sm font-mono">
                        {formatEuropeanDateTime(log.timestamp)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getActionIcon(log.action)}
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getActionBadgeColor(log.action)}`}>
                            {getActionLabel(log.action)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-white text-sm">
                            {log.details?.stopped_by_name || log.details?.initiated_by_name || log.actor_id?.slice(0, 8)}
                          </p>
                          {log.details?.stopped_by_role || log.details?.initiated_by_role ? (
                            <p className="text-xs text-zinc-500">
                              {log.details?.stopped_by_role || log.details?.initiated_by_role}
                            </p>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          log.actor_role === 'SUPER_ADMIN' ? 'bg-red-500/20 text-red-400' :
                          log.actor_role === 'ADMIN' ? 'bg-purple-500/20 text-purple-400' :
                          log.actor_role === 'MODERATOR' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-zinc-500/20 text-zinc-400'
                        }`}>
                          {log.actor_role}
                        </span>
                      </TableCell>
                      <TableCell className="text-zinc-300 text-sm max-w-md">
                        <p className="truncate">{formatDetails(log.details)}</p>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedLog(log)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Log Details Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white font-outfit flex items-center gap-2">
              {selectedLog && getActionIcon(selectedLog.action)}
              Detajet e Regjistrimit
            </DialogTitle>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-xs text-zinc-500 uppercase">Koha</p>
                  <p className="text-white font-mono">{formatEuropeanDateTime(selectedLog.timestamp)}</p>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-xs text-zinc-500 uppercase">Veprimi</p>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getActionBadgeColor(selectedLog.action)}`}>
                    {getActionLabel(selectedLog.action)}
                  </span>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-xs text-zinc-500 uppercase">Aktori</p>
                  <p className="text-white">{selectedLog.details?.stopped_by_name || selectedLog.details?.initiated_by_name || selectedLog.actor_id}</p>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-xs text-zinc-500 uppercase">Roli</p>
                  <span className={`px-2 py-0.5 rounded text-xs ${
                    selectedLog.actor_role === 'ADMIN' ? 'bg-purple-500/20 text-purple-400' : 'bg-zinc-500/20 text-zinc-400'
                  }`}>
                    {selectedLog.actor_role}
                  </span>
                </div>
              </div>
              
              <div className="bg-zinc-800/50 rounded-lg p-4">
                <p className="text-xs text-zinc-500 uppercase mb-2">Detaje të Plota</p>
                <pre className="text-sm text-zinc-300 overflow-auto max-h-64 whitespace-pre-wrap font-mono bg-zinc-900 rounded p-3">
                  {JSON.stringify(selectedLog.details, null, 2)}
                </pre>
              </div>
              
              {selectedLog.target_id && (
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-xs text-zinc-500 uppercase">ID e Objektit</p>
                  <code className="text-emerald-400 font-mono">{selectedLog.target_id}</code>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditLogs;
